define('util/resize-observer',[
	"./class",
	"./dom-queues",
	"./event/capture"
], function( Class, DomQueues, capture ) {

	"use strict";

	function memoize( fn ) {
		var
			cache = undefined;

		function memoized() {
			return ( cache !== undefined ) ? cache : ( cache = fn());
		}

		memoized.reset = function() { cache = undefined; }
		return memoized;
	}

	var indexOf = "indexOf" in Array
		? function( arr, item, from ) { return arr.indexOf( item, from || 0 ); }
		: function( arr, item, from ) {
			var i, n;

			for ( i = from || 0, n = arr.length ; i !== n ; ++i ) {
				if ( arr[ i ] === item ) return i;
			}

			return -1;
		};

	// ===========================================================================================

	// ResizeObserver requires injection of a stylesheet, because it needs to set properties that
	// cannot be set via inline CSS.
	var injectStyles = memoize( function() {
		var style = document.createElement( "style" );

		style.innerHTML = [
			"/* Injected by ResizeObserver */",
			"resize-observer {display:inline;width:0;height:0;overflow:hidden;visibility:hidden;-ms-overflow-style:none;-webkit-flex:0 0 0;flex:0 0 0;-webkit-animation:resize-observer-init .1s steps(1);animation:resize-observer-init .1s steps(1)}",
			"resize-observer::-webkit-scrollbar,resize-observer-expandee::-webkit-scrollbar,resize-observer-shrinkee::-webkit-scrollbar {display: none}",
			"resize-observer-layer {display:block;position:absolute;overflow:hidden;width:100%;height:100%;left:0;top:0;}",
			"resize-observer-layer > resize-observer-layer {width:auto;height:auto;left:auto;top:auto;}",
			"resize-observer-expandee, resize-observer-shrinkee {display:block;position:absolute;overflow:scroll;visibility:hidden;width:100%;height:100%;}",
			"resize-observer-expander {display:block;position:absolute;left:0;top:0;}",
			"resize-observer-shrinker {display:block;position:absolute;width:200%;height:200%;}",
			"@-webkit-keyframes resize-observer-init { 0% {opacity:0} 100% {opacity:1}}",
			"@keyframes resize-observer-init { 0% {opacity:0} 100% {opacity:1}}"
		].join( "\n" );

		document.head.appendChild( style );

		return style;
	});

	var hasResizeEvent = memoize( function() {
		var win = window;
		return (( "attachEvent" in win ) && !( "MutationObserver" in win ));
	});

	var getScrollbarSizes = memoize( function() {
		var
			doc    = document,
			body   = document.body,
			testee = doc.createElement( "resize-observer" );

		// Ensure the resize observer style rules are injected.
		injectStyles();

		// Overrides a few of the injected style properties for this test.
		testee.style.cssText = "display:block;width:500px;height:500px;overflow:scroll;";

		body.insertBefore( testee, body.firstChild );

		var width  = testee.offsetWidth  - testee.clientWidth;
		var height = testee.offsetHeight - testee.clientHeight;

		body.removeChild( testee );
		testee = null;

		return {
			width  : width,
			height : height
		}
	});

	// ===========================================================================================

	function getWidthOffset() {
		return 2 * getScrollbarSizes().width + 1;
	}

	function getHeightOffset() {
		return 2 * getScrollbarSizes().height + 1;
	}

	function getExpandWidth(width) {
		return width + 10 + getWidthOffset();
	}

	function getExpandHeight(height) {
		return height + 10 + getHeightOffset();
	}

	function getShrinkWidth(width) {
		return width * 2 + getWidthOffset();
	}

	function getShrinkHeight(height) {
		return height * 2 + getHeightOffset();
	}

	function getStyle( element ) {
		var style = window.getComputedStyle( element );

		return {
			position  : style.position,
			top       : style.top,
			right     : style.right,
			bottom    : style.bottom,
			left      : style.left,
		};
	}

	function getTRBL( top, right, bottom, left ) {
		top    = ( !top    ? "0" : ( top    + "px" ));
		right  = ( !right  ? "0" : ( right  + "px" ));
		bottom = ( !bottom ? "0" : ( bottom + "px" ));
		left   = ( !left   ? "0" : ( left   + "px" ));

		return "top:" + top + ";right:" + right + ";bottom:" + bottom + ";left:" + left + ";"
	}

	// ===========================================================================================

	var ResizeObservation = Class.create({
		constructor : function( id, element ) {
			var me = this;

			me.id        = id;
			me.state     = { width : 0, height : 0 };
			me.container = null;
			me.injected  = false;

			me.listeners = [];
			me.unbinders = [];

			me.injectContainer( element );
		},

		destroy : function() {
			var me = this,
				unbinders = this.unbinders,
				unbinder;

			while ( unbinder = unbinders.pop()) {
				unbinder();
			}

			me.container.parentNode.removeChild( me.container );
			me.disposing = true;

			delete ResizeObservation.instances[ me.id ];
		},

		injectContainer : function( element ) {
			var me = this,
				doc       = element.ownerDocument,
				container = me.container = doc.createElement( "resize-observer" );

			me.unbinders.push( capture( container, "animationstart", function( event ) {
				if ( event.target !== container ) return;

				DomQueues
					.read( function(){ return me.updateState( element ) })
					.then( function( state ) {
						if ( !me.injected ) {
							me.injected = true;
							return me.injectScrollers( element, container, state );
						} else {
							return me.updateScrollers( container, state );
						}
					})
					.then( function() {
						// Always notify on animationstart, since either the element just
						// became visible or the resize observer just attached to it.
						me.notify();
					});
			}));

			if ( element.firstChild != null ) {
				element.insertBefore( container, element.firstChild );
			} else {
				element.appendChild( container );
			}
		},

		injectScrollers : function( element, container, state ) {
			var
				me    = this,
				doc   = element.ownerDocument,
				style,
				bars,
				expandee,
				shrinkee;

			DomQueues.read( function() {
				style = getStyle( element );
				bars  = getScrollbarSizes();
			});

			return DomQueues.write( function( cancel ) {
				if ( me.disposing ) {
					cancel();
					return;
				}

				// WORKAROUND:
				//  WebKit bug https://bugs.webkit.org/show_bug.cgi?id=80808 requires that we add an outer
				//  layer with a top and left anchor point of 0 and a width and height of 100%, inside
				//  which the actual layer resides with a full set of top; right; bottom; and left anchor
				//  points.
				//
				//  Due to Webkit bug https://bugs.webkit.org/show_bug.cgi?id=152980 the absolute positioned
				//  layer may occasionally be less wide than the targeted element when the element is an
				//  inline element.
				//  Any problems with this are resolved by expanding the inner layer, in such a way that it
				//  will always be atleast 1x1 px in size.
				var
					outerLayer = doc.createElement( "resize-observer-layer"    ),
					layer      = doc.createElement( "resize-observer-layer"    ),
					expander   = doc.createElement( "resize-observer-expander" ),
					shrinker   = doc.createElement( "resize-observer-shrinker" );


				expandee = doc.createElement( "resize-observer-expandee" );
				shrinkee = doc.createElement( "resize-observer-shrinkee" );

				if ( style.position === "static" ) {
					[ "top","right","bottom","left" ].forEach( function( prop ) {
						var value = style[ prop ];
						if ( value !== "auto" && value !== "0px" ) {
							element.style[ prop ] = "0";
							console.warn( "ResizeObserver: style property '"+prop+"' has been reset to 0 to prevent side-effects.");
						}
					});

					element.style.position = "relative";
				}

				expandee.appendChild( expander );
				shrinkee.appendChild( shrinker );

				layer.style.cssText = getTRBL( -(1 + bars.height), -bars.width, -bars.height, -(1 + bars.width));
				layer.appendChild( expandee );
				layer.appendChild( shrinkee );

				outerLayer.appendChild(layer);
				container.appendChild(outerLayer);

				return me.updateScrollers( container, state );
			}).then( function( cancel ) {
				if ( me.disposing ) {
					cancel();
					return;
				}

				function handleScroll() {
					me.handleScroll( element, container );
				}

				me.unbinders.push( capture( expandee, "scroll", handleScroll ));
				me.unbinders.push( capture( shrinkee, "scroll", handleScroll ));
			});
		},

		updateState : function( element ) {
			var state = this.state;

			state.lastWidth    = state.width;
			state.lastHeight   = state.height;
			state.width        = element.offsetWidth;
			state.height       = element.offsetHeight;

			return state;
		},

		handleScroll : function( element, container ) {
			var me = this;

			if ( me.isUpdatingScroll ) {
				me.hasScrollScheduled = true;
			} else {
				me.isUpdatingScroll = true;
				me.hasScrollScheduled = false;

				DomQueues
					.read( function(){ return me.updateState( element ) })
					.then( function( state ) {
						me.updateScrollers( container, state ).then( function() {
							if (( state.width !== state.lastWidth ) || ( state.height !== state.lastHeight )) {
								me.notify();
							}

							me.isUpdatingScroll = false;
							if ( me.hasScrollScheduled ) { me.handleScroll( element, container ); }
						});
					});
			}
		},

		updateScrollers : function( container, state ) {
			var me = this,
				shrinkee,
				expandee,
				expander,
				expandWidth,
				expandHeight,
				shrinkWidth,
				shrinkHeight;

			return DomQueues.write( function( cancel ) {
				if ( me.disposing ) return;

				shrinkee = container.querySelector( "resize-observer-shrinkee" ),
				expandee = container.querySelector( "resize-observer-expandee" ),
				expander = container.querySelector( "resize-observer-expander" ),

				expandWidth  =  getExpandWidth( state.width  ),
				expandHeight = getExpandHeight( state.height ),
				shrinkWidth  =  getShrinkWidth( state.width  ),
				shrinkHeight = getShrinkHeight( state.height );

				expander.style.width  = expandWidth  + "px";
				expander.style.height = expandHeight + "px";

			}).then( function() {
				// Setting a scroll offset involves a read of the scrollWidth
				// and/or scrollHeight property, both of which have just been
				// updated when setting `expander.style`.
				// A read/write swap is inserted here to move the following
				// write operation that sets the scroll offset into the next
				// animation frame, preventing layout thrashing.
				return DomQueues.read() 
			}).then( function() {
				return DomQueues.write( function() {
					if ( me.disposing ) return;

					expandee.scrollLeft = expandWidth;
					expandee.scrollTop = expandHeight;

					shrinkee.scrollLeft = shrinkWidth;
					shrinkee.scrollTop  = shrinkHeight;
				});
			});
		},

		addListener : function( listener ) {
			var me = this,
				listeners = me.listeners;

			if ( -1 === indexOf( listeners, listener )) {
				listeners.push( listener );
			}
		},

		removeListener : function( listener ) {
			var me = this,
				listeners = me.listeners,
				index     = indexOf( listeners, listener );

			if ( -1 !== index ) {
				listeners.splice( index, 1 );
			}

			if ( listeners.length === 0 ) {
				me.destroy();
			}
		},

		notify : function() {
			var me = this,
				state     = me.state,
				listeners = me.listeners,
				i, n;

			for( i = 0, n = listeners.length ; i !== n ; ++i ) {
				listeners[ i ]();
			}

		}
	});

	ResizeObservation.uuid      = (function(uuid){ return function() { return "_" + ( ++uuid ); }}(0));
	ResizeObservation.instances = {};
	ResizeObservation.for = function( element ) {
		var
			id        = element.__resizeObservation,
			instances = ResizeObservation.instances,
			instance;

		if ( id && ( instance = instances[ id ])) { return instance; }

		// Ensure the stylesheet has been injected and the scrollbar size has been memoized.
		injectStyles();
		getScrollbarSizes();

		id = element.__resizeObservation = ResizeObservation.uuid();
		instances[ id ] = instance = new ResizeObservation( id, element );

		return instance;
	}

	// ===========================================================================================

	function ResizeObserverEntry( target ) {
		var me = this;

		me.target       = target;
		me.clientWidth  = target.clientWidth;
		me.clientHeight = target.clientHeight;
	}

	function toEntries( targets ) {
		var
			entries = [],
			mark    = "__@resize-observer@__",
			target,
			i,
			n;

		for ( i = 0, n = targets.length ; i !== n ; ++i ) {
			target = targets[ i ];

			// already processed
			if ( mark in target ) continue;

			entries[ mark ] = true;
			entries.push( new ResizeObserverEntry( target ));
		}

		for ( n = entries.length ; --n !== -1 ; ) {
			target = entries[ n ].target;
			delete target[ mark ];
		}

		return entries;
	}

	function observe( target, listener ) {
		var observation;

		// Old Internet Explorer versions can listen directly for the resize
		// event on DOM elements.
		if ( hasResizeEvent()) {

			target.attachEvent( "onresize", listener );
			return function() { target.detachEvent( "onresize", listener ) };

		} else {

			observation = ResizeObservation.for( target );
			observation.addListener( listener );
			return function() { observation.removeListener( listener ); }
		}
	}

	/**
	 * @module {util.ResizeObserver} util/resize-observer ResizeObserver
	 * @parent util.modules
	 * @alias Observer
	 *
	 * Exports a class that provides a way to react to changes in the size
	 * of DOM elements.
	 *
	 * @signature `new ResizeObserver(callback)`
	 * Creates a new ResizeObserver.
	 *
	 * @param {function(Array.<ResizeChangeEntry>)} callback(records)
	 * The function which will be called for each DOM element size change.
	 * The observer will call this function with two arguments:
	 *
	 * @option {Array.<ResizeChangeEntry>} records
	 * An array of change records, representing the DOM elements that
	 * changed in size.
	 *
	 * @option {ResizeObserver} observer
	 * The observer itself.
	 */
	var ResizeObserver = Class.create({
		constructor : function ResizeObserver( callback ) {
			var me = this,
				scheduled = false;

			me.__observed  = [];
			me.__active    = [];
			me.__unbinders = [];

			me.__callback = function() {
				if ( scheduled ) return;

				scheduled = true;

				DomQueues
					.read(function() {
						var targets = me.__active;

						return toEntries( targets.splice( 0, targets.length ));
					})
					.then( function( entries ) {
						DomQueues.write( function() {
							if ( entries.length !== 0 ) {
								callback.call( me, entries, me );
							}

							scheduled = false;

							// If more active targets were collected while this pass
							// was being processed, then reschedule in another pass.
							if ( me.__active.length !== 0 ) {
								me.__callback();
							}
						});
					});
			};
		},

		/** @prototype */

		/**
		 * Registers the ResizeObserver instance to receive notifications
		 * of size changes on the specified DOM element.
		 *
		 * @param {Element} target
		 * The DOM element on which to observe size changes.
		 */
		observe : function( target ) {
			var me = this,
				observed = me.__observed,
				active   = me.__active;

			if ( !target.nodeType ) {
				throw new Error( "Not a valid HTML element" );
			} 

			if ( /^(?:area|br|colgroup|col|embed|hr|img|input|link|meta|param|source|object)$/i.test( target.nodeName )) {
				throw new Error( "Cannot observe contentless elements." );
			}

			if ( /^(?:table|tbody|thead|tfoot|tr)$/i.test( target.nodeName )) {
				throw new Error( "Cannot observe table-related elements." );
			}

			if ( -1 === indexOf( observed, target )) {
				observed.push( target );

				me.__unbinders.push( observe( target, function resizeObserverListener() {
					if ( -1 === indexOf( active, target )) {
						active.push( target );
						me.__callback();
					}
				}));
			}
		},

		/**
		 * Stops this ResizeObserver instance from receiving notifications
		 * of size changes on the specified DOM element.
		 *
		 * @param {Element} target
		 * The DOM element on which to no longer observe size changes.
		 */
		unobserve : function( target ) {
			var me = this,
				index = indexOf( me.__observed, target );

			if ( index !== -1 ) {
				me.__unbinders.splice( index, 1 )[ 0 ]();
				me.__observed.splice( index, 1 );

				// Discard any pending changes for this DOM element, of which
				// there may be multiple.
				index = 0;
				do {
					index = indexOf( me.__active, target, index );
					if ( index !== -1 ) {
						me.__active.splice( index, 1 );
					}
				} while ( index !== -1 );
			}
		},

		/**
		 * Stops this ResizeObserver instance from receiving notifications
		 * of size changes on DOM elements.
		 * Until the [util/resize-observer.prototype.observe observe] method
		 * is used again, the observer's callback will not be invoked.
		 */
		disconnect : function() {
			var me = this,
				unbinders = me.__unbinders,
				unbinder;

			// Discard pending changes and observed nodes by resetting
			// the array size.
			me.__observed.length = 0;
			me.__active.length   = 0;

			while ( unbinder = unbinders.pop()) {
				unbinder();
			}
		}
	});


	return ResizeObserver;
});
